<script setup>
import { ref, computed, watch, onMounted, toRef, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import TipTapEditor2 from "@/features/document-editor/ui/TipTapEditor2.vue";
import { useApplicationParticipants } from "@/entities/application";
import { useParticipantTypes } from "@/entities/directory";
import { StepRunList, useTransactionLinks } from "@/entities/transaction";

import AddParticipantHeader from "../components/AddParticipantHeader.vue";
import ParticipantList from "../components/ParticipantList.vue";
import AddParticipantToInscription from "../components/AddParticipantToInscription.vue";
import GbdflManualBlock from "../components/GbdflManualBlock.vue";
import ServiceExecutionBlock from "../components/ServiceExecutionBlock.vue";
import PlaceholderMappingDialog from "../components/PlaceholderMappingDialog.vue";
import { ParticipantDropdown } from "@/features/participant-dropdown";

import { useServiceQueueStore } from "@/store/serviceQueue";
import { ServiceQueueStepStatus } from "@/shared/config/serviceQueueStepStatuses";
import { ParticipantRole } from "@/shared/config/participantRoles";
import { updateMentionsInAllLanguages } from "@/shared/mention";
import {
  normalizeRuntimeResponse,
  autoSelectSingleItemFields,
} from "@/shared/serviceResponse";
import { filterStepsForParticipant } from "@/shared/lib/queueStepFilter";
import { toast } from "@/shared/store/alertStore";
import { useConfirm } from "@/shared/ui/alert-dialog";
import {
  mapRepeatSectionInAllLanguages,
  removeRepeatSectionByCodeInAllLanguages,
  restoreOriginalRepeatSection,
} from "@/shared/mention/placeholderReplacer";

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
  uuid: {
    type: String,
    default: null,
  },
  template: {
    type: Object,
    default: () => ({}),
  },
  editable: {
    type: Boolean,
    default: false,
  },
  welcome: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:content",
  "change:tab",
  "refresh",
  "refresh-participants",
]);

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const serviceQueue = useServiceQueueStore();
const confirm = useConfirm();

const editorRef = ref(null);
const templateLang = ref("ru");

const contentKey = computed(() => {
  return {
    kz: "content_kz",
    ru: "content",
    en: "content_en",
  }[templateLang.value];
});

const templateJson = computed(() => {
  let json = props.form?.[contentKey.value];
  if (typeof json === "string") {
    try {
      return JSON.parse(json);
    } catch (error) {
      console.error("Invalid template JSON", error);
      return null;
    }
  }
  return json;
});

const editorMode = computed(() => (props.editable ? "edit" : "view"));

function changeLang(lang) {
  templateLang.value = lang;
}

function onUpdateJson(json) {
  if (!props.editable) return;
  emit("update:content", { lang: templateLang.value, json });
}

watch(
  () => props.form?.languages,
  (langs) => {
    if (Array.isArray(langs) && langs.length) {
      if (!langs.includes(templateLang.value)) {
        templateLang.value = langs[0];
      }
    }
  },
  { immediate: true }
);

// ===== Участники =====

const uuidRef = toRef(props, "uuid");
const {
  participants,
  participantsLoading,
  fetchParticipants,
  addParticipant,
  updateParticipant,
  removeParticipant,
} = useApplicationParticipants(uuidRef);

const { participantTypes, fetchParticipantTypes } = useParticipantTypes();

const {
  links: attorneyLinks,
  linksLoading,
  fetchLinks,
} = useTransactionLinks(uuidRef);

const visibleAddParticipant = ref(false);
const addType = ref(null); // "INDIVIDUAL" | "LEGAL"
const selectedRole = ref(null);

const visibleEditParticipant = ref(false);
const editingParticipant = ref(null);

function onSelectRole({ type, role }) {
  if (!props.editable) return;
  addType.value = type;
  selectedRole.value = role;
  visibleAddParticipant.value = true;
  if (uuidRef.value) getExecution();
}

function closeAddParticipant() {
  visibleAddParticipant.value = false;
  addType.value = null;
  selectedRole.value = null;
}

async function onSaveParticipant(payload) {
  const response = await addParticipant(payload);
  if (uuidRef.value) await getExecution();
  emit("refresh-participants");
  closeAddParticipant();

  const responsePayload = response?.payload || response?.data?.payload || null;
  const newUuid = responsePayload?.uuid;
  const fromList = newUuid
    ? participants.value.find((p) => p.uuid === newUuid)
    : null;
  const addedParticipant = fromList || {
    ...payload,
    ...(responsePayload || {}),
  };

  if (addedParticipant?.uuid) {
    selectedParticipant.value = addedParticipant;
    activeService.value = getLastGroupItemCode(addedParticipant);

    maybeStartLegalRecovererQueue(addedParticipant);

    autoInsertParticipantFio(addedParticipant);

    const placeholderCode =
      props.template?.default_participant_code ||
      props.form?.default_participant_code;

    if (placeholderCode) {
      placeholderMappingData.value = {
        placeholders: [placeholderCode],
        participant: addedParticipant,
      };
      placeholderDialogVisible.value = true;
    }
  }
}

function confirmPlaceholderMapping(placeholder, newCode) {
  const participant = placeholderMappingData.value.participant;

  const participantType = participantTypes.value.find(
    (pt) => pt.code === participant?.participant_type_code
  );
  const typeName = participantType?.name || participantType?.name_kz || "";
  const fio = participant?.first_name
    ? `${participant.last_name || ""} ${participant.first_name || ""}`.trim()
    : "";
  const label = typeName ? `${typeName}${fio ? ": " + fio : ""}` : newCode;

  const updatedForm = mapRepeatSectionInAllLanguages(
    props.form,
    placeholder,
    newCode,
    label
  );

  ["content", "content_kz", "content_en"].forEach((key) => {
    if (updatedForm[key] !== undefined) {
      props.form[key] = updatedForm[key];
    }
  });
  if (updatedForm._repeatSectionTemplate !== undefined) {
    props.form._repeatSectionTemplate = updatedForm._repeatSectionTemplate;
  }

  placeholderDialogVisible.value = false;
  placeholderMappingData.value = { placeholders: [], participant: null };

  nextTick(() => {
    const currentContent = props.form[contentKey.value];
    if (editorRef.value?.editor && currentContent) {
      editorRef.value.editor.commands.setContent(currentContent);
    }
    // Mentions теперь имеют корректный participant_type_code —
    // повторно заполняем их данными участника.
    autoInsertParticipantFio(participant);
  });
}

function skipPlaceholderMapping() {
  placeholderDialogVisible.value = false;
  placeholderMappingData.value = { placeholders: [], participant: null };
}

function openMentionDropdown({
  participant_type_code,
  onSave,
  onDelete,
  onRestore,
  onNumberToWords,
  hasValue,
  rect,
}) {
  if (!participantDropdownRef.value) return;
  const selectedCodes = [
    ...new Set(participants.value.map((p) => p.participant_type_code)),
  ];
  participantDropdownRef.value.openDropdown({
    selectedType: participant_type_code,
    selectedCodes,
    onSave,
    onDelete,
    onRestore,
    onNumberToWords,
    hasValue,
    rect,
  });
}

async function onRemoveParticipant(participant) {
  if (!participant?.uuid) return;

  serviceQueue.cancelQueue(participant.uuid);

  await removeParticipant(participant.uuid);
  if (uuidRef.value) await getExecution();
  emit("refresh-participants");

  if (!participant.participant_type_code) return;

  let updatedForm = removeRepeatSectionByCodeInAllLanguages(
    props.form,
    participant.participant_type_code
  );

  const currentDoc = updatedForm[contentKey.value];
  const hasAnySections = currentDoc?.content?.some(
    (n) => n.type === "repeatSection"
  );

  if (!hasAnySections && updatedForm._repeatSectionTemplate) {
    updatedForm = restoreOriginalRepeatSection(updatedForm);
  }

  ["content", "content_kz", "content_en"].forEach((key) => {
    if (updatedForm[key] !== undefined) {
      props.form[key] = updatedForm[key];
    }
  });

  nextTick(() => {
    const currentContent = props.form[contentKey.value];
    if (editorRef.value?.editor && currentContent) {
      editorRef.value.editor.commands.setContent(currentContent);
    }
  });
}

// ===== StepRun execution =====

const execution = ref(null);
const stepsEntityTypes = ref(null);
const isAccessLoading = ref(false);

async function getExecution() {
  if (!uuidRef.value) return;
  isAccessLoading.value = true;
  try {
    const response = await StepRunList(uuidRef.value);
    if (!response?.success) return;
    execution.value = response.payload?.execution || null;
    if (execution.value?.steps_entity_types) {
      stepsEntityTypes.value = execution.value.steps_entity_types;
    }
  } finally {
    isAccessLoading.value = false;
  }
}

// ===== Сервисы =====

const activeService = ref(null);
const selectedParticipant = ref(null);
const requestedServices = ref(new Map());
const requestedServicesData = ref(new Map());

function buildRequestedServiceKey() {
  return selectedParticipant.value.uuid + ":" + activeService.value;
}

function onServiceResponseError() {
  if (!selectedParticipant.value?.uuid || !activeService.value) return;
  requestedServices.value.set(
    buildRequestedServiceKey(),
    ServiceQueueStepStatus.ERROR
  );
}

function onServiceResponseChanged(data) {
  if (!selectedParticipant.value?.uuid || !activeService.value) return;
  const key = buildRequestedServiceKey();
  requestedServices.value.set(key, ServiceQueueStepStatus.SUCCESS);
  requestedServicesData.value.set(key, data || null);
}

async function onAttorneyLinked() {
  await Promise.all([fetchLinks(), fetchParticipants()]);
  if (uuidRef.value) await getExecution();
}
const savingParticipant = ref(false);
const placeholderDialogVisible = ref(false);
const placeholderMappingData = ref({ placeholders: [], participant: null });
const participantDropdownRef = ref(null);

function onSelectStep(item, participant) {
  if (!item?.code) return;
  const sameParticipant =
    !participant || participant.uuid === selectedParticipant.value?.uuid;
  if (!sameParticipant) {
    selectedParticipant.value = participant;
    activeService.value = item.code;
    return;
  }
  if (activeService.value === item.code) {
    activeService.value = null;
    return;
  }
  activeService.value = item.code;
}

function onSelectParticipant(participant) {
  const prevUuid = selectedParticipant.value?.uuid;
  selectedParticipant.value = participant;
  if (prevUuid !== participant?.uuid) {
    activeService.value = getLastGroupItemCode(participant);
  }
}

function handleServiceLoadingStart({
  service_code,
  participant_type_code,
} = {}) {
  if (!service_code) return;
  nextTick(() => {
    editorRef.value?.setMentionsLoading?.({
      serviceCode: service_code,
      participantTypeCode: participant_type_code,
    });
  });
}

function handleServiceLoadingEnd({ service_code, participant_type_code } = {}) {
  nextTick(() => {
    editorRef.value?.clearMentionsLoading?.({
      serviceCode: service_code,
      participantTypeCode: participant_type_code,
    });
  });
}

function applyFieldsToForm(fields) {
  if (!fields?.length) return;
  const updatedForm = updateMentionsInAllLanguages(props.form, fields);
  ["content", "content_kz", "content_en"].forEach((key) => {
    if (updatedForm[key] !== undefined) {
      props.form[key] = updatedForm[key];
    }
  });
}

function insertFieldsAsMentions(fields) {
  if (!Array.isArray(fields) || fields.length === 0) return;
  applyFieldsToForm(fields);
  nextTick(() => {
    editorRef.value?.insertServiceFields(fields);
  });
}

function onInsertField(field) {
  if (!field) return;
  insertFieldsAsMentions([field]);
}

function onInsertFields(fields) {
  insertFieldsAsMentions(fields);
}

async function onInsertSavedFields(participant) {
  if (!participant || !props.editable) return;
  const serviceFields = participant.service_fields;
  if (!serviceFields || typeof serviceFields !== "object") return;

  const entries = Object.entries(serviceFields).filter(
    ([, payload]) =>
      payload && typeof payload === "object" && Object.keys(payload).length > 0
  );
  if (!entries.length) return;

  const collected = [];
  for (const [serviceCode, payload] of entries) {
    const options = {
      locale: locale.value || "ru",
      participantTypeCode: participant.participant_type_code,
      serviceCode,
    };
    const baseFields = normalizeRuntimeResponse(payload, options);
    const autoFields = autoSelectSingleItemFields(payload, options);
    collected.push(...baseFields, ...autoFields);
  }

  if (!collected.length) {
    toast.info(t("Нет данных для вставки"), { duration: 2000 });
    return;
  }

  const participantFio =
    [participant.last_name, participant.first_name, participant.middle_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    participant.params?.company_name ||
    "";

  try {
    await confirm(
      `У ${participantFio} сохранено данных: ${collected.length}. Подставить их в текст документа?`,
      t("Подтверждение"),
      {
        confirmButtonText: t("Вставить"),
        cancelButtonText: t("Отмена"),
      }
    );
  } catch {
    return;
  }

  insertFieldsAsMentions(collected);
  toast.success(t("Данные ГБД"), {
    description: `${collected.length} значений вставлено`,
    duration: 2500,
  });
}

function onEditParticipant(participant) {
  if (!participant?.uuid || !props.editable) return;
  editingParticipant.value = participant;
  visibleEditParticipant.value = true;
}

const welcomeHandled = ref(false);

function tryOpenWelcomeEditor() {
  if (!props.welcome || welcomeHandled.value) return;
  if (!props.editable) return;
  if (visibleEditParticipant.value || visibleAddParticipant.value) return;

  const recoverer = participants.value.find(
    (p) => p.participant_type_code === ParticipantRole.RECOVERER
  );
  if (!recoverer) return;

  welcomeHandled.value = true;
  onEditParticipant(recoverer);

  if (route.query.welcome) {
    const query = { ...route.query };
    delete query.welcome;
    router.replace({ query });
  }
}

function closeEditParticipant() {
  visibleEditParticipant.value = false;
  editingParticipant.value = null;
}

async function onUpdateParticipant({ uuid, payload }) {
  if (!uuid) return;
  try {
    const response = await updateParticipant(uuid, payload);
    if (!response?.success) {
      toast.error(t("Не удалось сохранить данные участника"));
      return;
    }
    const fresh = participants.value.find((p) => p.uuid === uuid);
    if (fresh && selectedParticipant.value?.uuid === uuid) {
      selectedParticipant.value = fresh;
    }

    maybeStartLegalRecovererQueue(fresh || { uuid, ...payload });

    emit("refresh-participants");
    toast.success(t("Сохранено"));
    closeEditParticipant();
  } catch (e) {
    console.error("onUpdateParticipant error:", e);
    toast.error(t("Не удалось сохранить данные участника"));
  }
}

async function onSaveParticipantData({ uuid, payload }) {
  if (!uuid || savingParticipant.value) return;
  savingParticipant.value = true;
  try {
    const response = await updateParticipant(uuid, payload);
    if (!response?.success) {
      toast.error(t("Не удалось сохранить данные участника"));
      return;
    }
    const fresh = participants.value.find((p) => p.uuid === uuid);
    if (fresh) selectedParticipant.value = fresh;
    emit("refresh-participants");
    toast.success(t("Сохранено"));
  } catch (e) {
    console.error("onSaveParticipantData error:", e);
    toast.error(t("Не удалось сохранить данные участника"));
  } finally {
    savingParticipant.value = false;
  }
}

function getLastGroupItemCode(participant) {
  if (!participant) return null;
  if (participant.participant_type_code === ParticipantRole.RECOVERER) {
    const rawSteps =
      stepsEntityTypes.value?.[participant.participant_type] || [];
    const stepList = filterStepsForParticipant(
      rawSteps,
      participant.participant_type_code
    );
    if (stepList.length) return stepList[stepList.length - 1]?.code || null;
    return "manual_input";
  }
  return "manual";
}

function maybeStartLegalRecovererQueue(participant) {
  if (!participant?.uuid) return;
  if (participant.participant_type !== "LEGAL") return;
  if (participant.participant_type_code !== ParticipantRole.RECOVERER) return;
  if (!execution.value?.uuid || !route.params.id) return;

  const legalSteps = stepsEntityTypes.value?.LEGAL || [];
  const gbdulStep = legalSteps.find((s) => s.code === "GBDULBasic");
  if (!gbdulStep) return;

  const bin = participant.params?.company_bin || participant.identifier || "";
  if (!bin || bin.length !== 12) return;

  const subject = store.state.self_user?.subject || {};
  // ИИН представителя: если participant сохранён через actsForLegal,
  // то participant.identifier — это IIN; иначе берём IIN подписанта из ЭЦП.
  const personIdentifier =
    (participant.params?.company_bin ? participant.identifier : "") ||
    subject.iin ||
    "";

  const label =
    participant.params?.company_name ||
    [participant.last_name, participant.first_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "";

  serviceQueue.startQueue({
    participantUuid: participant.uuid,
    participantType: participant.participant_type,
    participantTypeCode: participant.participant_type_code,
    participantLabel: label,
    executionUuid: execution.value.uuid,
    txId: route.params.id,
    identifier: personIdentifier || bin,
    personIdentifier,
    businessIdentifier: bin,
    steps: [
      {
        uuid: gbdulStep.uuid,
        code: gbdulStep.code,
        name: gbdulStep.name,
        participant_type_code: participant.participant_type_code,
      },
    ],
    onStepStart: ({ stepCode, participantTypeCode }) => {
      handleServiceLoadingStart({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
      });
    },
    onStepSuccess: ({ stepCode, data, participantTypeCode }) => {
      const fields = normalizeRuntimeResponse(data, {
        locale: locale.value,
        participantTypeCode,
        serviceCode: stepCode,
      });
      if (fields.length) insertFieldsAsMentions(fields);
      handleServiceLoadingEnd({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
      });
    },
    onStepError: ({ stepCode, participantTypeCode }) => {
      handleServiceLoadingEnd({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
      });
    },
  });
}

function autoInsertParticipantFio(participant) {
  if (!participant) return;
  if (participant.participant_type !== "INDIVIDUAL") return;
  if (participant.participant_type_code === ParticipantRole.RECOVERER) return;

  const dict = (val) =>
    val ? { name: val, name_ru: val, name_kk: val } : null;
  const params = participant.params || {};
  const reg = params.reg_address || {};
  const birth = params.birth_address || {};

  const payload = {
    iin: participant.identifier || null,
    first_name: participant.first_name || null,
    last_name: participant.last_name || null,
    middle_name: participant.middle_name || null,
    birth_date: params.birth_date || null,
    reg_address: {
      country: dict(reg.country),
      region: dict(reg.region),
      district: dict(reg.district),
      city: reg.city || null,
      street: reg.street || null,
      building: reg.building || null,
      corpus: reg.corpus || null,
      flat: reg.flat || null,
    },
    birth_address: {
      country: dict(birth.country),
      region: dict(birth.region),
      district: dict(birth.district),
      city: birth.city || null,
    },
  };

  const fields = normalizeRuntimeResponse(payload, {
    locale: locale.value,
    participantTypeCode: participant.participant_type_code,
    serviceCode: "manual",
  });

  if (fields.length) insertFieldsAsMentions(fields);
}

watch(
  uuidRef,
  (val) => {
    if (val) {
      fetchParticipants();
      getExecution();
    }
  },
  { immediate: true }
);

watch(
  participants,
  (list) => {
    if (!list?.length) return;
    if (!selectedParticipant.value) {
      selectedParticipant.value = list[0];
      activeService.value = getLastGroupItemCode(list[0]);
    }
    tryOpenWelcomeEditor();
  },
  { immediate: true }
);

watch(
  () => props.editable,
  () => tryOpenWelcomeEditor()
);

onMounted(() => {
  if (!props.welcome) return;
  setTimeout(async () => {
    if (welcomeHandled.value) return;
    await fetchParticipants();
    tryOpenWelcomeEditor();

    if (welcomeHandled.value) return;
    setTimeout(async () => {
      if (welcomeHandled.value) return;
      await fetchParticipants();
      tryOpenWelcomeEditor();
    }, 3000);
  }, 2000);
});

function insertFieldsExternal(fields) {
  insertFieldsAsMentions(fields);
}

defineExpose({ insertFieldsExternal });

onMounted(() => {
  fetchParticipantTypes();
});
</script>

<template>
  <div class="template">
    <div class="block">
      <TipTapEditor2
        ref="editorRef"
        :mode="editorMode"
        :templateLang="templateLang"
        :json="templateJson"
        @change:lang="changeLang"
        @update:json="onUpdateJson"
        @edit:mention="openMentionDropdown"
      />
    </div>

    <div class="block h-full">
      <div class="inner h-full flex">
        <div v-if="participants?.length" class="flex w-full h-full">
          <div
            class="border border-0 border-r border-solid border-border w-1/2 h-full"
          >
            <div class="p-6 h-full overflow-y-auto">
              <GbdflManualBlock
                v-if="activeService === 'manual'"
                :selected-participant="selectedParticipant"
                :saving="savingParticipant"
                @insert:field="onInsertField"
                @insert:fields="onInsertFields"
                @save:participant="onSaveParticipantData"
              />

              <ServiceExecutionBlock
                v-else
                :uuid="uuid"
                :form="form"
                :execution="execution"
                :activeStepCode="activeService"
                :template="template"
                :selected-participant="selectedParticipant"
                :participant_type="selectedParticipant?.participant_type"
                @insert:field="onInsertField"
                @insert:fields="onInsertFields"
                @service-loading-start="handleServiceLoadingStart"
                @service-loading-end="handleServiceLoadingEnd"
                @service-response-error="onServiceResponseError"
                @service-response-changed="onServiceResponseChanged"
                @attorney-linked="onAttorneyLinked"
                @change:tab="(tab) => emit('change:tab', tab)"
              />
            </div>
          </div>

          <div class="w-1/2 h-full">
            <div class="flex flex-col gap-6 p-6 h-full overflow-y-auto">
              <AddParticipantHeader
                :template="template"
                :participantTypes="participantTypes"
                :participants="participants"
                @select="onSelectRole"
              />

              <ParticipantList
                :participants="participants"
                :participantTypes="participantTypes"
                :stepsEntityTypes="stepsEntityTypes"
                :loading="participantsLoading"
                :isAccessLoading="isAccessLoading"
                :activeService="activeService"
                :selectedParticipant="selectedParticipant"
                :requestedServices="requestedServices"
                :requestedServicesData="requestedServicesData"
                :attorneyLinks="attorneyLinks"
                :attorneyLinksLoading="linksLoading"
                :editable="editable"
                @update-data="onRemoveParticipant"
                @select:step="onSelectStep"
                @select:participant="onSelectParticipant"
                @edit:participant="onEditParticipant"
                @open:attorney-links="fetchLinks"
                @insert:saved-fields="onInsertSavedFields"
              />
            </div>
          </div>
        </div>

        <div v-else class="w-full h-full">
          <div class="flex flex-col gap-6 p-6 h-full">
            <AddParticipantHeader
              :template="template"
              :participantTypes="participantTypes"
              :participants="participants"
              @select="onSelectRole"
            />

            <div
              class="w-full p-4 bg-card flex flex-col items-start justify-center gap-4"
              style="height: calc(100vh - 330px)"
            >
              <div class="w-full flex flex-col items-center gap-2.5">
                <svg-icon
                  name="huge-user-plus"
                  class="w-8 h-8 text-muted-foreground"
                />
                <div
                  class="text-muted-foreground text-sm font-medium leading-5"
                >
                  {{ $t("Добавьте участника") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddParticipantToInscription
      v-if="visibleAddParticipant"
      :participantType="addType"
      :selectedRole="selectedRole"
      :execution="execution"
      @close="closeAddParticipant"
      @save="onSaveParticipant"
    />

    <AddParticipantToInscription
      v-if="visibleEditParticipant && editingParticipant"
      :editingParticipant="editingParticipant"
      :execution="execution"
      @close="closeEditParticipant"
      @save="onUpdateParticipant"
    />

    <ParticipantDropdown ref="participantDropdownRef" />

    <PlaceholderMappingDialog
      v-model:visible="placeholderDialogVisible"
      :placeholders="placeholderMappingData.placeholders"
      :participant="placeholderMappingData.participant"
      @confirm="confirmPlaceholderMapping"
      @skip="skipPlaceholderMapping"
    />
  </div>
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;

  .block {
    @apply flex flex-col w-1/2 h-full  gap-6 overflow-hidden;
    background-color: hsl(var(--background));
    border-radius: var(--radius);

    min-height: 600px !important;

    box-shadow: 0 1px 1px -0.5px rgba(0, 0, 0, 0.03),
      0 3px 3px -1.5px rgba(0, 0, 0, 0.03),
      0 20px 20px -12px rgba(0, 0, 0, 0.03);

    > .inner {
      @apply flex-1 overflow-auto;
    }
  }
}

@media (max-width: 1400px) {
  .template {
    @apply flex-col;

    .block {
      @apply w-full;
    }
  }
}
</style>
